import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_ENDPOINTS } from '@g99-product-web-ui/shared-util-lib';
import { Observable } from 'rxjs';

import { ServiceType } from '../../constants/service-types.enum';
import { HttpService } from '../../core/http-service/http.service';

export interface FlattenedPost extends IAiSocialMediaGenericPostSuggestion {
  sessionId: string;
  sessionType: 'festival' | 'generic';
  sessionTimestamp: string;
  festivalName: string;
  festivalDate: string;
  id: number;
}

export interface IAiSocialMediaGenericPostSuggestion {
  content: string;
  hashtags?: string[];
  imageUrl?: string;
  isGeneratingImage?: boolean;
}

export interface IAiSocialMediaSavePostsPayload {
  [date: string]: Array<{
    id: string;
    type: 'generic' | 'festival';
    posts: Array<{
      content: string;
      hashtags?: string[];
      festivalName?: string;
      festivalDate?: string;
    }>;
  }>;
}

// Interfaces

export interface StoredSocialPost {
  id: string;
  type: 'generic' | 'festival';
  posts: IAiSocialMediaGenericPostSuggestion[];
  timestamp: string;
  label: string;
}

export interface SocialAiPostBatchResponse {
  totalCount: number;
  page: number;
  size: number;
  totalPages: number;
  posts: SocialAiPostResponse[];
}

export interface SocialAiPostResponse {
  generatedDate: string;
  id: number;
  content: string;
  hashtags: string[];
  festivalName?: string;
  festivalDate?: string;
  status: string;
  type: 'FESTIVAL' | 'GENERIC';
  imageUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SocialPostStorageService {
  protected readonly STORAGE_KEY = 'socialMediaPosts';

  constructor(private readonly _httpService: HttpService) {}

  /**
   * Save generated posts to database
   */
  public savePosts(
    posts: FlattenedPost[],
    type: 'festival' | 'generic'
  ): Observable<void> {
    const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const uniqueId = `${type}-${Date.now()}`;

    const payload: IAiSocialMediaSavePostsPayload = {
      [currentDate]: [
        {
          id: uniqueId,
          type,
          posts: posts.map((post) => ({
            content: post.content,
            hashtags: post.hashtags,
            ...(type === 'festival' &&
              'festivalName' in post && {
                festivalName: post.festivalName,
                festivalDate: post.festivalDate,
              }),
          })),
        },
      ],
    };

    return this._httpService.post(
      API_ENDPOINTS.AI_SOCIAL_ENDPOINTS.savePostToDb,
      payload,
      { attachTenantId: true },
      ServiceType.DEFAULT
    );
  }

  /**
   * Get saved posts with server-side pagination and search. type is sent as FESTIVAL or GENERIC.
   */
  public getPosts(
    page: number,
    size: number,
    search?: string,
    type?: 'FESTIVAL' | 'GENERIC'
  ): Observable<SocialAiPostBatchResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('search', (search ?? '').trim());

    if (type) {
      params = params.set('type', type);
    }

    return this._httpService.get<SocialAiPostBatchResponse>(
      API_ENDPOINTS.AI_SOCIAL_ENDPOINTS.getDraftPosts,
      {
        params,
        attachTenantId: true,
      },
      ServiceType.DEFAULT
    );
  }
}
